import React, { useCallback, useMemo, useState } from 'react';
import { Box, FormControlLabel, Switch, TextField, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

import Container from './styles/Container';
import Scale from './styles/Scale';
import SliderWrapper from './styles/SliderWrapper';
import ToggleRow from './styles/ToggleRow';
import ThresholdSlider from './styles/ThresholdSlider';
import calculateSegments from './utils/calculateSegments';
import clampThresholds from './utils/clampThresholds';
import computeScaleBounds from './utils/computeScaleBounds';

function ThresholdSliderWidget({
  id,
  label,
  minValue,
  maxValue,
  warningLower,
  warningUpper,
  errorLower,
  errorUpper,
  warningEnabled,
  errorEnabled,
  onChange,
  className,
  disabled,
}) {
  const theme = useTheme();

  const colors = useMemo(
    () => ({
      alarm: '#ef9a9a',
      warning: '#ffcc80',
      normal: '#a5d6a7',
      alarmMain: '#e57373',
      warningMain: '#ffb74d',
    }),
    [theme],
  );

  const displayBounds = useMemo(
    () =>
      computeScaleBounds({
        warningLower: warningLower ?? minValue,
        warningUpper: warningUpper ?? maxValue,
        errorLower: errorLower ?? minValue,
        errorUpper: errorUpper ?? maxValue,
      }),
    [minValue, maxValue, warningLower, warningUpper, errorLower, errorUpper],
  );

  const [scaleMin, setScaleMin] = useState(displayBounds.displayMin);
  const [scaleMax, setScaleMax] = useState(displayBounds.displayMax);

  const clamped = useMemo(
    () =>
      clampThresholds({
        minValue: scaleMin,
        maxValue: scaleMax,
        errorLower: errorLower ?? minValue,
        errorUpper: errorUpper ?? maxValue,
        warningLower: warningLower ?? minValue,
        warningUpper: warningUpper ?? maxValue,
        warningEnabled: !!warningEnabled,
        errorEnabled: !!errorEnabled,
      }),
    [scaleMin, scaleMax, minValue, maxValue, errorLower, errorUpper, warningLower, warningUpper, warningEnabled, errorEnabled],
  );

  const segments = useMemo(() => calculateSegments(clamped, colors), [clamped, colors]);

  const totalRange = clamped.maxValue - clamped.minValue || 1;

  const emitChange = useCallback(
    (patch) => {
      onChange({
        id,
        minValue: clamped.minValue,
        maxValue: clamped.maxValue,
        warningLower: clamped.warningLower,
        warningUpper: clamped.warningUpper,
        errorLower: clamped.errorLower,
        errorUpper: clamped.errorUpper,
        warningEnabled: clamped.warningEnabled,
        errorEnabled: clamped.errorEnabled,
        ...patch,
      });
    },
    [id, clamped, onChange],
  );

  const handleMinChange = useCallback(
    (e) => {
      const val = Number(e.target.value);
      if (Number.isNaN(val)) return;
      setScaleMin(val);
    },
    [],
  );

  const handleMaxChange = useCallback(
    (e) => {
      const val = Number(e.target.value);
      if (Number.isNaN(val)) return;
      setScaleMax(val);
    },
    [],
  );

  const handleErrorChange = useCallback(
    (_, newValue) => {
      emitChange(clampThresholds({ ...clamped, errorLower: newValue[0], errorUpper: newValue[1] }));
    },
    [clamped, emitChange],
  );

  const handleWarningChange = useCallback(
    (_, newValue) => {
      emitChange(clampThresholds({ ...clamped, warningLower: newValue[0], warningUpper: newValue[1] }));
    },
    [clamped, emitChange],
  );

  const handleWarningToggle = useCallback(
    (_, checked) => {
      emitChange(clampThresholds({ ...clamped, warningEnabled: checked }));
    },
    [clamped, emitChange],
  );

  const handleErrorToggle = useCallback(
    (_, checked) => {
      emitChange(clampThresholds({ ...clamped, errorEnabled: checked }));
    },
    [clamped, emitChange],
  );

  return (
    <Container className={className}>
      {label && (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          type="number"
          value={Math.round(clamped.minValue * 100) / 100}
          onChange={handleMinChange}
          disabled={disabled}
          sx={{
              width: 80,
              mt: 0.5,
              mr: 1,
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          size="small"
          label="Мин"
        />

        <SliderWrapper sx={{ pt: '24px' }}>
          <Scale>
            {segments.map((seg, idx) => (
              <Box
                key={seg.label}
                sx={{
                  flex: ((seg.end - seg.start) / totalRange) * 100,
                  backgroundColor: seg.color,
                  height: '100%',
                  transition: theme.transitions.create('flex'),
                  borderRight: idx < segments.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                }}
              />
            ))}
          </Scale>

          {clamped.errorEnabled && (
            <ThresholdSlider
              value={[clamped.errorLower, clamped.errorUpper]}
              onChange={handleErrorChange}
              min={clamped.minValue}
              max={clamped.maxValue}
              step={1}
              valueLabelDisplay="on"
              disabled={disabled}
              customColor={colors.alarmMain}
              sx={{ top: '24px' }}
            />
          )}

          {clamped.warningEnabled && (
            <ThresholdSlider
              value={[clamped.warningLower, clamped.warningUpper]}
              onChange={handleWarningChange}
              min={clamped.minValue}
              max={clamped.maxValue}
              step={1}
              valueLabelDisplay="on"
              disabled={disabled}
              customColor={colors.warningMain}
              sx={{ top: '24px' }}
            />
          )}
        </SliderWrapper>

        <TextField
          type="number"
          value={Math.round(clamped.maxValue * 100) / 100}
          onChange={handleMaxChange}
          disabled={disabled}
          sx={{
              width: 80,
              mt: 0.5,
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          size="small"
          label="Макс"
        />
      </Box>

      <ToggleRow>
        <FormControlLabel
          control={<Switch checked={clamped.warningEnabled} onChange={handleWarningToggle} disabled={disabled} size="small" />}
          label={<Typography variant="body2">Предупреждение</Typography>}
        />
        <FormControlLabel
          control={<Switch checked={clamped.errorEnabled} onChange={handleErrorToggle} disabled={disabled} size="small" />}
          label={<Typography variant="body2">Тревога</Typography>}
        />
      </ToggleRow>
    </Container>
  );
}

ThresholdSliderWidget.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  warningLower: PropTypes.number,
  warningUpper: PropTypes.number,
  errorLower: PropTypes.number,
  errorUpper: PropTypes.number,
  warningEnabled: PropTypes.bool,
  errorEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

ThresholdSliderWidget.defaultProps = {
  label: '',
  warningLower: null,
  warningUpper: null,
  errorLower: null,
  errorUpper: null,
  warningEnabled: false,
  errorEnabled: false,
  className: '',
  disabled: false,
};

export default ThresholdSliderWidget;